import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import ProfileInfoCard from 'examples/Cards/InfoCards/ProfileInfoCard';

import Header from 'layouts/profile/components/Header';

import { useMe } from 'core/me/provider/MeProvider';
import { openSupportWhatsapp, SUPPORT_PHONE } from 'shared/utils/support';

/* -------------------- TR yazımlar -------------------- */
function trPlanName(rawPlan) {
  const p = (rawPlan || '').toString().trim().toUpperCase();
  if (!p) return '—';

  if (p === 'TRIAL') return 'Deneme';
  if (p === 'ENTERPRISE') return 'Kurumsal';
  if (p === 'PRO') return 'Pro';
  if (p === 'BASIC') return 'Temel';

  // bilinmeyen plan: aynen göster
  return p;
}

function trPlanStatus(rawStatus) {
  const s = (rawStatus || '').toString().trim().toUpperCase();
  if (!s) return '—';

  if (s === 'ACTIVE') return 'Aktif';
  if (s === 'PASSIVE') return 'Pasif';
  if (s === 'EXPIRED') return 'Süresi doldu';
  if (s === 'CANCELLED') return 'İptal';
  if (s === 'TRIAL') return 'Deneme';

  return s;
}

/* -------------------- pill -------------------- */
function getPlanPill(me) {
  const plan = (me?.plan || '').toString().toUpperCase();
  const status = (me?.planStatus || '').toString().toUpperCase();

  // TRIAL -> Deneme
  if (plan === 'TRIAL' || status === 'TRIAL') {
    return { text: 'Deneme', bg: 'warning.light', color: 'warning.dark' };
  }

  // ACTIVE -> Aktif
  if (status === 'ACTIVE') {
    return { text: 'Aktif', bg: 'success.light', color: 'success.dark' };
  }

  // diğerleri
  if (status === 'PASSIVE') return { text: 'Pasif', bg: 'grey.200', color: 'text.primary' };
  if (status === 'EXPIRED') return { text: 'Süresi doldu', bg: 'error.light', color: 'error.dark' };

  return null;
}

function Pill({ text, bg, color }) {
  return (
    <MDBox
      sx={{
        px: 1.2,
        py: 0.45,
        borderRadius: '999px',
        backgroundColor: bg,
        color,
        fontWeight: 800,
        fontSize: '0.72rem',
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {text}
    </MDBox>
  );
}

function ProfileOverview() {
  const { me } = useMe();

  const fullName =
    (me?.fullName && me.fullName.trim()) || (me?.name && me.name.trim()) || 'Kullanıcı';

  const email = (me?.email && me.email.trim()) || '—';
  const mobile = (me?.phone && me.phone.trim()) || '—';

  const companyName =
    (me?.companyName && me.companyName.trim()) ||
    (me?.company?.name && String(me.company.name).trim()) ||
    'İşletme';

  const planRaw = me?.plan;
  const statusRaw = me?.planStatus;

  const planTR = trPlanName(planRaw); // ✅ TRIAL -> Deneme vs
  const statusTR = trPlanStatus(statusRaw); // ✅ ACTIVE -> Aktif vs

  const address =
    (me?.companyAddress && String(me.companyAddress).trim()) ||
    (me?.address && String(me.address).trim()) ||
    (me?.city && String(me.city).trim()) ||
    '—';

  const supportText = `Merhaba, ${companyName} için destek almak istiyorum. Tel: ${SUPPORT_PHONE}`;
  const pill = getPlanPill(me);
  const noopAction = {
    route: '', // önemli: undefined olmasın
    tooltip: '', // boş olabilir
    onClick: () => {}, // hiçbir şey yapmasın
  };
  return (
    <Header>
      {/* ✅ ÜST SATIR: İşletme + Pill + Destek (ayrı) */}
      <MDBox
        px={2}
        pt={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
      >
        <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
          <MDTypography variant="h6" fontWeight="medium" sx={{ minWidth: 0 }}>
            {companyName}
          </MDTypography>
          {pill ? <Pill {...pill} /> : null}
        </MDBox>

        <MDButton
          variant="gradient"
          color="success"
          size="small"
          startIcon={<Icon>chat</Icon>}
          onClick={() => openSupportWhatsapp(supportText)}
          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
        >
          WhatsApp Destek
        </MDButton>
      </MDBox>

      <MDBox mt={3} mb={2} px={2}>
        <Grid container spacing={2} alignItems="stretch">
          {/* ✅ PROFİL */}
          <Grid item xs={12} md={6}>
            <ProfileInfoCard
              title="Profil Bilgileri"
              description="Hesabınıza ait temel bilgiler"
              info={{
                'Ad Soyad': fullName,
                Telefon: mobile,
                'E-posta': email,
              }}
              social={[]}
              action={noopAction} // ✅ ekle
              shadow={false}
            />
          </Grid>

          {/* ✅ İŞLETME */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}
            />

            <MDBox sx={{ width: '100%' }}>
              <ProfileInfoCard
                title="İşletme Bilgileri"
                description="İşletme ve paket bilgileriniz"
                info={{
                  'İşletme Adı': companyName,
                  Paket: `${planTR} • ${statusTR}`,
                  Adres: address,
                }}
                social={[]}
                action={noopAction} // ✅ ekle
                shadow={false}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      {/* ✅ ÖZET */}
      <MDBox pt={1} px={2} lineHeight={1.25}>
        <MDTypography variant="h6" fontWeight="medium">
          Özet
        </MDTypography>
        <MDTypography variant="button" color="text">
          {companyName} • Paket: {planTR} • Durum: {statusTR}
        </MDTypography>
      </MDBox>
    </Header>
  );
}

export default ProfileOverview;
